import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Button } from "../../buttons";
import classNames from "classnames";
import styles from "../MultiFilter/MultiFilter.module.scss";
import {
  ComponentType,
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useControllableState } from "../../../helpers/state/state-hooks";
import { IconClose, IconFilterSort, IconProps } from "../../icons";
import { ButtonExpandable } from "../../buttons/button-expandable/button-expandable";
import { useUpdateEffect } from "usehooks-ts";
import { Indicator } from "../Indicator/Indicator";

export interface MultiFilterProps {
  /** Determines if the filter is currently disabled. Defaults to false */
  disabled?: boolean;
  /** Determines if the filter is currently open. Defaults to false */
  open?: boolean;
  /** Text to display within the button trigger */
  placeholder?: string;
  /** Callback which invoked when the filter's open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Text to display below the filter input */
  helperText?: string;
  /** Icon which is displayed beside the placeholder text within the button trigger */
  triggerIcon?: ComponentType<IconProps>;
  /** Callback which is invoked when the filter's Apply button is pressed **/
  onApply?: () => void;
  /** Callback which is invoked when the filter's Cancel button is pressed **/
  onCancel?: () => void;

  value?: string[];

  onChange?: (value: string[]) => void;

  className?: string;

  asComponentOnly?: boolean;

  /**An escape hatch to declare what keys create a new entry. Enter and comma are the default delimiters.
   * The following site is good to determine the key code needed for a given key press, reference the event.key that is emitted:
   * https://www.toptal.com/developers/keycode
   * */
  delimiters?: string[];
}

export const MultiFilter = (props: PropsWithChildren<MultiFilterProps>) => {
  const {
    disabled,
    open,
    placeholder,
    onOpenChange,
    helperText,
    triggerIcon = IconFilterSort,
    onApply,
    onChange,
    onCancel,
    value,
    className,
    asComponentOnly = false,
    delimiters = ["Enter", ","],
  } = props;

  const [isOpen, setOpen] = useControllableState({
    prop: open,
    defaultProp: false,
    onChange: onOpenChange,
  });

  const [appliedValues, setAppliedValues] = useControllableState({
    prop: value,
    defaultProp: [],
    onChange: onChange,
  });

  const T = triggerIcon;

  const GetTriggerIcon = () => {
    if (T && disabled)
      return <T className={`${styles.icon} ${styles.triggerIconDisabled}`} />;
    else if (T) return <T className={`${styles.icon} ${styles.filterIcon}`} />;
  };

  const dropdownClasses = classNames(styles.content);

  const [enteredValues, setEnteredValues] = useState<string[]>(appliedValues);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState(false);

  const handleInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (!inputRef.current) return;

    if (
      event.key === "Backspace" &&
      !inputRef.current.value &&
      enteredValues.length > 0
    ) {
      setEnteredValues((prev) => prev.slice(0, prev.length - 1));
      return;
    }

    if (delimiters.includes(event.key)) {
      event.preventDefault();
      const enteredValue = inputRef.current.value.trim();
      if (enteredValue) {
        setEnteredValues((prevValues) => [...prevValues, enteredValue]);
        inputRef.current.value = "";
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!inputRef.current) return;

    const trimmedInput = event.target.value.trim();
    const blacklistedDelimiters = ["Enter"];

    const actualDelimiters = delimiters
      .filter((delimiter) => !blacklistedDelimiters.includes(delimiter))
      .filter((delimiter) => delimiter !== ""); // Keep only non-empty strings

    const delimiterPattern = actualDelimiters
      .map((delimiter) => `\\${delimiter}`)
      .join("|");

    if (delimiterPattern === "") {
      return [trimmedInput];
    }

    const delimiterRegex = new RegExp(delimiterPattern, "ig");

    const delimitedInputs = trimmedInput
      .split(delimiterRegex)
      .filter((x) => x)
      .map((x) => x.trim());

    if (delimitedInputs.length > 1) {
      setEnteredValues((prevValues) => [...prevValues, ...delimitedInputs]);
      inputRef.current.value = "";
    }
  };

  const handleClear = () => {
    setEnteredValues([]);

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.selectionStart = 0;
    }
  };

  const removeSelectedValue = (index: number) => {
    setEnteredValues((prev) => prev.filter((x, ind) => ind !== index));
  };

  const handleApply = () => {
    setAppliedValues(enteredValues);
    setOpen(false);
    onApply?.();
  };

  const handleCancel = () => {
    setEnteredValues(appliedValues);
    setOpen(false);
    onCancel?.();
  };

  useUpdateEffect(() => {
    if (asComponentOnly) {
      setAppliedValues(enteredValues);
    }
  }, [enteredValues]);

  useEffect(() => {
    setEnteredValues(appliedValues);
  }, [appliedValues]);

  const isDirty = useMemo(() => {
    if (enteredValues.length !== appliedValues.length) {
      return true;
    }

    return !enteredValues.every(
      (value, index) => value === appliedValues[index],
    );
  }, [enteredValues, appliedValues]);

  const label = useMemo(() => {
    if (appliedValues.length === 0) return placeholder;
    return appliedValues.join(", ");
  }, [appliedValues]);

  const inputClasses = classNames(styles.filterInput, {
    [styles.inputContainerFocused]: isFocused,
  });

  const triggerClasses = classNames(styles.trigger, className);

  const input = (
    <>
      <div
        className={styles.inputContainer}
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        <div className={inputClasses}>
          {enteredValues.map((value, index) => {
            return (
              <Fragment key={index}>
                <div
                  className={styles.enteredValue}
                  style={{
                    flexGrow: value.length,
                  }}
                  title={value}
                >
                  <span className={styles.enteredValueText}>{value}</span>
                  <button
                    onClick={(e) => {
                      removeSelectedValue(index);
                      e.stopPropagation();
                    }}
                    className={styles.pillCloseButton}
                  >
                    <IconClose className={styles.iconClose} />
                  </button>
                </div>
              </Fragment>
            );
          })}
          <input
            ref={inputRef}
            onKeyDown={handleInputKeyPress}
            className={styles.unstyledInput}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            autoFocus={!asComponentOnly}
          />
        </div>
      </div>

      <div>
        <div className={styles.helperText}>
          <span>{helperText}</span>
        </div>
      </div>
    </>
  );

  return !asComponentOnly ? (
    <Popover.Root open={isOpen} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <ButtonExpandable
          aria-label="Open filter dropdown"
          variant="secondary"
          disabled={disabled}
          expanded={isOpen}
          expandedChevron={null}
          collapsedChevron={null}
          className={triggerClasses}
        >
          <GetTriggerIcon />
          <span className={styles.filterLabel}>{label}</span>
          {appliedValues.length > 0 && (
            <Indicator count={appliedValues.length} />
          )}
        </ButtonExpandable>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={"start"}
          sticky={"always"}
          className={dropdownClasses}
        >
          <div className={styles.filterContainer}>
            {input}
            <hr className={styles.lineBreak}></hr>
            <div className={styles.clearButtonContainer}>
              <Button
                aria-label="Clear filter input"
                className={styles.clearButton}
                variant="tertiary-blue"
                onClick={() => handleClear()}
              >
                Clear
              </Button>
              <div className={styles.buttonCoupleContainer}>
                <Button
                  aria-label="Close filter dropdown"
                  onClick={() => handleCancel()}
                  className={styles.buttonCouple}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  arial-label="Apply filter(s)"
                  onClick={() => handleApply()}
                  className={styles.buttonCouple}
                  variant="primary"
                  disabled={!isDirty}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  ) : (
    input
  );
};
